import React, { useState } from "react"
import { Link } from "react-router-dom"
import { useNavigateToPost } from "../../hooks/useNavigateToPost"
import { SmartDateFormat } from "../../util/dates"
import { PauseCircle, PlayCircle, PlusCircle } from "react-bootstrap-icons"
import { Spinner } from "../shared/Spinner"
import { RequestError } from "../shared/RequestError"
import { compareByKey } from "../../util/utils"
import CommentSectionPreview from "../comments/CommentSectionPreview"
import type { AlbumDisplayConfig } from "../../types/albumDisplay"
import { useDispatch, useSelector } from "react-redux"
import {
  prependAndPlay,
  playOrPause,
} from "../../stores/mediaPlayerSlice"
import { openSongActions } from "../../stores/songActionsSlice"
import { SingleTrackItem } from "./SingleTrackItem"
import { usePost } from "../../hooks/usePost"
import { AnchorButton, ShowHideButton } from "../shared/Button"
import { ReactionBlock } from "./ReactionBlock"
import RelatedPosts from "../RelatedPosts"

const styles = {
  header: "text-base md:text-2xl font-bold text-black-800 leading-none md:leading-tight",
  bandName: "text-gray-500 leading-none text-xs",
  postDate: "text-xs md:text-sm text-black-800 leading-none md:leading-normal",
  externalLink: "text-xs md:text-sm text-gray-500 italic",
  description: "text-xs md:text-sm text-gray-500 mt-1",
}

const ICON_SIZE = "1.6rem"
const MARGIN_SPACING = "0.4rem 0.5rem 0.4rem 0"

interface AlbumListItemShortProps {
  album: any
  adminMode?: boolean
  updateStatus?: () => void
  config?: AlbumDisplayConfig
}

export const AlbumListItemShort = ({
  album,
  adminMode = false,
  updateStatus = () => {},
  config = { showComments: "full" },
}: AlbumListItemShortProps) => {
  const { showComments } = config
  const [postStatus, setPostStatus] = useState(album.status)
  const [imageHovered, setImageHovered] = useState(false)
  const albumDetailData = usePost(album.id, "songs")
  const albumDetails = albumDetailData?.data?.post
  const navigateToPostScreen = useNavigateToPost(album.nada_path)

  const dispatch = useDispatch()
  const currentMediaItem = useSelector(
    // @ts-ignore
    (state) => state.mediaPlayer.value.currentMediaItem,
  )
  // @ts-ignore
  const playerState = useSelector(
    (state: any) => state.mediaPlayer.value.playerState,
  )
  const isCurrentlyPlaying =
    playerState === "playing" && currentMediaItem?.albumId === album.id
  const isCurrentlyQueued =
    playerState === "paused" && currentMediaItem?.albumId === album.id

  const sortedSongs = () => {
    return [...albumDetails.songs]
      .sort((a: any, b: any) => compareByKey(a, b, "track_number"))
      .map((song: any) => ({
        ...song,
        album_name: albumDetails.title,
        cover_image_small: albumDetails.cover_image_small,
      }))
  }

  const onPlayClick = () => {
    dispatch(prependAndPlay(sortedSongs()))
  }
  const onPlayOrPauseClick = () => {
    dispatch(playOrPause())
  }
  const onAddClick = () => {
    dispatch(openSongActions({ ...sortedSongs()[0], albumId: album.id }))
  }

  return (
    <div>
      <div className="flex gap-3 items-start">
        <div className="w-1/3 flex-shrink-0 relative">
          <img
            src={album.external_photo_src}
            alt={album.title}
            className="aspect-square object-cover w-full"
          />
          <button
            className="absolute inset-0 flex items-center justify-center focus:outline-none"
            style={{ background: imageHovered ? "rgba(0,0,0,0.15)" : "transparent", transition: "background 0.15s" }}
            onMouseEnter={() => setImageHovered(true)}
            onMouseLeave={() => setImageHovered(false)}
            onClick={isCurrentlyPlaying ? onPlayOrPauseClick : (isCurrentlyQueued ? onPlayOrPauseClick : onPlayClick)}
            title={isCurrentlyPlaying ? "Pause" : "Play"}
          >
            {isCurrentlyPlaying ? (
              <PauseCircle className="text-white drop-shadow w-10 h-10 md:w-20 md:h-20" />
            ) : (
              <PlayCircle className="text-white drop-shadow w-10 h-10 md:w-20 md:h-20" />
            )}
          </button>
        </div>

        <div className="flex-1 min-w-0">
          <h2
            className={styles.header + " cursor-pointer min-w-0"}
            onClick={navigateToPostScreen}
          >
            {album.title}
          </h2>

          <Link className={styles.bandName} to={album.local_path}>
            {album.band_name}
          </Link>

          <div className={styles.postDate}>
            {!album.is_released ? (
              <span>
                Announced&nbsp;
                {SmartDateFormat({ dateStr: album.announced_at, includeTime: false })}
                &nbsp;for Release on&nbsp;
                {SmartDateFormat({ dateStr: album.published_at, includeTime: false })}
              </span>
            ) : (
              <span>
                Released&nbsp;
                {SmartDateFormat({ dateStr: album.published_at, includeTime: false })}
              </span>
            )}
          </div>

          <div className={styles.externalLink}>
            {album.class_name === "Bandcamp" ? (
              <div className="mt-1">
                <AnchorButton href={album.external_url} target="_blank">
                  Buy on&nbsp;{album.class_name}
                </AnchorButton>
              </div>
            ) : album.class_name === "BandNada" &&
              album.bn_exclusive_until &&
              new Date(album.bn_exclusive_until) > new Date() ? (
              <span className="font-bold text-bn-premiere-green">
                exclusively on bandNada
              </span>
            ) : (
              <a href={album.external_url} target="_blank" rel="noreferrer">
                view on&nbsp;{album.class_name}
              </a>
            )}
          </div>

          <div className="flex items-center mt-1">
            <PlusCircle
              className="cursor-pointer flex-shrink-0"
              title="More options"
              style={{ minWidth: ICON_SIZE, width: ICON_SIZE, height: ICON_SIZE, margin: MARGIN_SPACING }}
              onClick={onAddClick}
            />
          </div>
        </div>
      </div>

      <div className="mt-2">
        {albumDetailData.status === "loading" ? (
          <Spinner className="text-gray-500" />
        ) : albumDetailData.status === "error" ? (
          <RequestError error={albumDetailData.error} />
        ) : (
          <>
            {albumDetails?.songs?.length > 0 ? (
              <ul className="text-sm text-black">
                {sortedSongs().map((song: any, index: number) => (
                  <SingleTrackItem key={song.id} song={song} index={index} />
                ))}
              </ul>
            ) : null}
          </>
        )}

        <div className="mt-4 hidden">
          <RelatedPosts postId={album.id} />
        </div>
        <ReactionBlock
          contentId={album.id}
          contentType="Post"
          postId={album.id}
        />
        <CommentSectionPreview
          postId={album.id}
          postPath={album.nada_url}
          mode={showComments}
        />
        {adminMode && (
          <ShowHideButton
            updateStatus={updateStatus}
            postStatus={postStatus}
            setPostStatus={setPostStatus}
          />
        )}
      </div>
    </div>
  )
}
